import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Target, Eye, Users, Linkedin, ArrowRight, Award, Globe, Heart } from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicAboutPage = () => {
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [aboutRes, settingsRes] = await Promise.all([
          axios.get(`${API}/cms/about`),
          axios.get(`${API}/cms/settings`)
        ]);
        setContent(aboutRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Failed to load about content');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const { mission, vision, team, partners, testimonials } = content || {};
  const leadershipTeam = team?.filter(t => t.is_leadership);
  const regularTeam = team?.filter(t => !t.is_leadership);

  return (
    <div className="min-h-screen bg-background">
      <MegaMenu />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-primary/10 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-primary text-sm font-medium mb-6">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Building Africa's<br />Sports Future
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto">
              Michezonet is Africa's leading sports ecosystem platform, providing 
              technology, services, and intelligence to transform the sports industry.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-card-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 bg-background border border-border rounded-2xl"
              data-testid="mission-section"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-white mb-4">Our Mission</h2>
              <p className="text-white/70 leading-relaxed">
                {mission || "To digitize and professionalize sports across Africa through innovative technology and services, empowering organizations to grow, engage fans, and achieve excellence."}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 bg-background border border-border rounded-2xl"
              data-testid="vision-section"
            >
              <div className="w-14 h-14 bg-secondary/10 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-secondary" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-white mb-4">Our Vision</h2>
              <p className="text-white/70 leading-relaxed">
                {vision || "To be the leading sports ecosystem platform powering the growth of African sports, connecting stakeholders, and driving the industry to new heights."}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">Our Values</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              The principles that guide everything we do at Michezonet.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Globe, title: 'Africa-First', desc: 'Built by Africans, for African sports. We understand the unique challenges and opportunities of our continent.' },
              { icon: Award, title: 'Excellence', desc: 'We strive for the highest standards in technology, service delivery, and stakeholder support.' },
              { icon: Heart, title: 'Impact', desc: 'Everything we do is aimed at creating meaningful, lasting positive impact on African sports.' }
            ].map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="p-6 bg-card-bg border border-border rounded-xl text-center"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-heading font-bold text-white mb-2">{value.title}</h3>
                <p className="text-white/60 text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      {leadershipTeam?.length > 0 && (
        <section className="py-20 bg-card-bg" id="team">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">Leadership Team</h2>
              <p className="text-white/60 max-w-2xl mx-auto">
                Meet the people driving Michezonet's vision forward.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {leadershipTeam.map((member, idx) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="group text-center"
                  data-testid={`team-member-${member.id}`}
                >
                  <div className="w-32 h-32 mx-auto mb-4 relative">
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full flex items-center justify-center overflow-hidden">
                      {member.avatar ? (
                        <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-4xl font-heading font-bold text-white/20">
                          {member.name?.charAt(0)}
                        </span>
                      )}
                    </div>
                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute -right-1 -bottom-1 w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Linkedin size={18} />
                      </a>
                    )}
                  </div>
                  <h3 className="text-lg font-heading font-bold text-white">{member.name}</h3>
                  <p className="text-primary text-sm font-medium">{member.role}</p>
                  {member.department && <p className="text-white/50 text-xs mt-1">{member.department}</p>}
                  {member.bio && (
                    <p className="text-white/60 text-sm mt-3 line-clamp-3">{member.bio}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Full Team */}
      {regularTeam?.length > 0 && (
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-heading font-bold text-white mb-2">Our Team</h2>
              <p className="text-white/60">The talented individuals behind Michezonet.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {regularTeam.map((member, idx) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.03 }}
                  viewport={{ once: true }}
                  className="text-center p-4"
                >
                  <div className="w-16 h-16 mx-auto mb-2 bg-primary/10 rounded-full flex items-center justify-center">
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.name} className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <span className="text-lg font-heading font-bold text-primary">
                        {member.name?.charAt(0)}
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm font-heading font-semibold text-white truncate">{member.name}</h4>
                  <p className="text-white/50 text-xs truncate">{member.role}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Partners */}
      {partners?.length > 0 && (
        <section className="py-20 bg-card-bg" id="partners">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">Our Partners</h2>
              <p className="text-white/60 max-w-2xl mx-auto">
                Working with leading organizations to grow African sports.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {partners.map((partner, idx) => (
                <motion.a
                  key={partner.id}
                  href={partner.website || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  viewport={{ once: true }}
                  className="p-6 bg-background border border-border rounded-xl flex items-center justify-center hover:border-primary/50 transition-all"
                  data-testid={`partner-${partner.id}`}
                >
                  {partner.logo ? (
                    <img src={partner.logo} alt={partner.name} className="max-h-12 object-contain" />
                  ) : (
                    <span className="text-lg font-heading font-bold text-white/40">{partner.name}</span>
                  )}
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials?.length > 0 && (
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">What People Say</h2>
              <p className="text-white/60 max-w-2xl mx-auto">
                Hear from organizations and professionals who work with Michezonet.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial, idx) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 bg-card-bg border border-border rounded-xl"
                  data-testid={`testimonial-${testimonial.id}`}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={`text-lg ${i < (testimonial.rating || 5) ? 'text-yellow-500' : 'text-white/20'}`}>
                        ★
                      </span>
                    ))}
                  </div>
                  <p className="text-white/80 italic mb-6">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      {testimonial.avatar ? (
                        <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover rounded-full" />
                      ) : (
                        <span className="text-sm font-heading font-bold text-primary">
                          {testimonial.name?.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{testimonial.name}</p>
                      <p className="text-white/50 text-xs">{testimonial.role}, {testimonial.organization}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/20 via-background to-secondary/20" id="careers">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Join Our Team
          </h2>
          <p className="text-xl text-white/70 mb-10">
            We're always looking for talented individuals passionate about 
            sports and technology to join our growing team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/jobs"
              className="px-8 py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all"
              data-testid="careers-cta"
            >
              View Open Positions <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 border border-white/20 text-white rounded-xl hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
};

export default PublicAboutPage;
